import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'
import {IThemeVariables} from '@toolify/client/src/modules/theme/types/IThemeVariables'
import {TextCanvasTableElementAlignType} from '@toolify/client/src/modules/tables/strategies/CanvasTableElementStrategies/TextCanvasTableElementStrategy/enum/TextCanvasTableElementAlignType'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'
import {TimeTrackerDropdownLayoutType} from '@toolify/client/src/modules/databases/enum/TimeTrackerDropdownLayoutType'
import {ChoosePropertyTypeLayoutType} from '@toolify/client/src/modules/databases/enum/ChoosePropertyTypeLayoutType'
import {StatusDropdownLayoutType} from '@toolify/client/src/modules/databases/enum/StatusDropdownLayoutType'
import {ItemGroupHeaderLayoutType} from '@toolify/client/src/modules/ui/enum/ItemGroupHeaderLayoutType'
import {
  FiordNightThemeVariables,
} from '@toolify/client/src/modules/theme/strategies/fiord-night/FiordNightThemeVariables'
import {
  FiordNightDerivedVariableType,
  getFiordNightDerivedVariables,
} from '@toolify/client/src/modules/theme/functions/getFiordNightDerivedVariables'
import {formatHex} from 'culori'

const lightnessAndHueOffsetValues = [
  [0.64, 0.16],
  [0.58, 0.14],
  [0.52, 0.12],
  [0.46, 0.10],
  // [0.4, 0.08],
]

// const hueArray = [240, 270, 300, 330, 345, 0, 30, 60, 90, 120, 150, 180, 210]
const hueArray = [240, 270, 300, 330, 0, 30, 60, 90, 120, 150, 180, 210]

const PaletteColors: string[] = lightnessAndHueOffsetValues.map(([lightness, hueOffset]) => {
  return [
    formatHex({
      mode: 'oklch',
      l: lightness,
      c: 0.01,
      h: 245,
      alpha: 1,
    }),
  ].concat(hueArray.map((hue) => {
    return formatHex({
      mode: 'oklch',
      l: lightness,
      // c: (0.16 - 0.01) * ((lightness - 0.15) / 0.4) + 0.01,
      c: hueOffset,
      h: hue,
      alpha: 1,
    })
  }))
}).flat()

const PaletteColorMatrix = lightnessAndHueOffsetValues.map((_, y) => {
  return (new Array(hueArray.length + 1)).fill(null).map((_, x) => {
    return (y * (hueArray.length + 1)) + x
  })
})

const DarkBaseThemeVariables: Omit<IThemeVariables, FiordNightDerivedVariableType> = {
  // fiord-night jest motywem bazowym - ponizej tylko to, czym dark sie od niego rozni
  ...FiordNightThemeVariables,

  [ThemeVariableType.PrimaryColor]: 'oklch(65% 0.1683 255)',
  [ThemeVariableType.PrimaryHoverColor]: 'oklch(70% 0.1683 255)',
  [ThemeVariableType.OnPrimaryColor]: '#d1dff6',
  [ThemeVariableType.PrimaryContainerColor]: 'oklch(28% 0.01 245)',
  [ThemeVariableType.PrimaryContainerHoverColor]: 'oklch(32% 0.01 245)',
  [ThemeVariableType.OnPrimaryContainerColor]: 'oklch(85% 0.01 245)',

  [ThemeVariableType.SecondaryColor]: '#677388',
  [ThemeVariableType.OnSecondaryColor]: '#cdd2db',
  [ThemeVariableType.SecondaryContainerColor]: '#404b5e',
  [ThemeVariableType.OnSecondaryContainerColor]: '#e8eaed',

  [ThemeVariableType.TertiaryColor]: '#9200c4',
  [ThemeVariableType.OnTertiaryColor]: '#e4b5ff',
  [ThemeVariableType.OnTertiaryContainerColor]: '#edd3fd',

  [ThemeVariableType.ErrorColor]: 'oklch(65% 0.1683 31)',
  [ThemeVariableType.OnErrorColor]: '#690005',
  [ThemeVariableType.ErrorContainerColor]: '#93000a',
  [ThemeVariableType.OnErrorContainerColor]: '#ffdad6',

  [ThemeVariableType.SurfaceDimColor]: 'oklch(16% 0.01 245)',
  [ThemeVariableType.SurfaceColor]: 'oklch(20% 0.01 245)',
  [ThemeVariableType.DatabaseTableGridColor]: 'oklch(20% 0.01 245)',

  [ThemeVariableType.ContainerHighestColor]: 'oklch(40% 0.01 245)',
  [ThemeVariableType.ContainerHighColor]: 'oklch(36% 0.01 245)',
  [ThemeVariableType.ContainerColor]: 'oklch(32% 0.01 245)',
  [ThemeVariableType.ContainerLowColor]: 'oklch(28% 0.01 245)',
  [ThemeVariableType.ContainerLowestColor]: 'oklch(24% 0.01 245)',

  [ThemeVariableType.ContentBrightColor]: 'oklch(85% 0.01 245)',
  [ThemeVariableType.ContentLightColor]: 'oklch(85% 0.01 245)',
  [ThemeVariableType.ContentNormalColor]: 'oklch(85% 0.01 245)',
  [ThemeVariableType.ContentDimColor]: 'oklch(65% 0.01 245)',
  [ThemeVariableType.ContentDarkColor]: 'oklch(50% 0.01 245)',
  [ThemeVariableType.ContentDarkestColor]: 'oklch(35% 0.01 245)',

  [ThemeVariableType.BorderRadius]: 8,
  [ThemeVariableType.CheckboxBorderRadius]: 8,
  [ThemeVariableType.Shadow]: 'none',
  [ThemeVariableType.MobileFontSizeFactor]: 1,

  [ThemeVariableType.DatabaseTableHeaderDefaultBackgroundColor]: 'oklch(28% 0.01 245)',
  [ThemeVariableType.DatabaseTableHeaderHoverBackgroundColor]: 'oklch(32% 0.01 245)',
  [ThemeVariableType.DatabaseTableStickyShadowColor]: 'oklch(20% 0.01 245)',
  [ThemeVariableType.DatabaseTableScrollShadowColor]: 'oklch(24% 0.01 245)',
  [ThemeVariableType.DatabaseCellHoverBackgroundColor]: 'oklch(36% 0.01 245)',
  [ThemeVariableType.DatabaseCellHoverBorder]: 'none',
  [ThemeVariableType.DatabaseCellFocusHoverBorder]: 'none',
  [ThemeVariableType.DatabaseRelationIconBackgroundColor]: 'transparent',
  [ThemeVariableType.DatabaseRelationIconBackgroundHoverColor]: 'transparent',
  [ThemeVariableType.DatabaseTableColumnNameAlignment]: TextCanvasTableElementAlignType.Left,
  [ThemeVariableType.FileIconWeightType]: IconWeightType.Solid,
  [ThemeVariableType.NotificationIconWeightType]: IconWeightType.Solid,
  [ThemeVariableType.PreferredIconWeightType]: IconWeightType.Solid,
  [ThemeVariableType.DatabaseHeaderGroupDropdownPlacement]: 'bottom-end',
  [ThemeVariableType.DashboardRecentListBackgroundColor]: 'transparent',
  [ThemeVariableType.DashboardRecentListHorizontalPadding]: '0',
  [ThemeVariableType.ShowDashboardRecentItemBorders]: false,
  [ThemeVariableType.UseStickyDashboardResourcesButton]: true,
  [ThemeVariableType.ShouldShowDashboardAndKanbanInContainer]: true,
  [ThemeVariableType.UseDetachedDashboardWidgetTypePreview]: true,
  [ThemeVariableType.CanCreateConversationDashboardWidget]: true,

  [ThemeVariableType.UseModernAuthLayout]: true,
  [ThemeVariableType.PreferBorders]: false,
  [ThemeVariableType.UseUnifiedMarkdownEditorBackground]: true,
  [ThemeVariableType.ShouldUseLineStyleInFilters]: true,
  [ThemeVariableType.ShowNewMessageButtonOutsideInput]: false,
  [ThemeVariableType.ShowSidebarWorkspaceDropdownToggle]: false,
  [ThemeVariableType.ShowSidebarFavouriteIconChips]: false,
  [ThemeVariableType.ShowCloudItemDetails]: false,
  [ThemeVariableType.ShowCloudItemIcon]: true,
  [ThemeVariableType.ShowEmptyFilesPlaceholderVisual]: true,
  [ThemeVariableType.ShowEmptyConversationPlaceholderVisual]: true,
  [ThemeVariableType.ShowWorkspaceInviteAsButton]: true,
  [ThemeVariableType.ShouldShowModalHeaderBackgrounds]: false,
  [ThemeVariableType.ShouldShowBreadcrumbsTitleFirst]: false,
  [ThemeVariableType.UsePaletteColorsInAvatars]: true,
  [ThemeVariableType.ShouldClampNotificationTitleItem]: false,
  [ThemeVariableType.ShowNotificationResourceSpace]: false,
  [ThemeVariableType.UseCenteredTimeTrackerCellLayout]: false,
  [ThemeVariableType.TimeTrackerDropdownLayoutType]: TimeTrackerDropdownLayoutType.Modern,
  [ThemeVariableType.ChoosePropertyTypeLayoutType]: ChoosePropertyTypeLayoutType.Modern,
  [ThemeVariableType.StatusDropdownLayoutType]: StatusDropdownLayoutType.Modern,
  [ThemeVariableType.ItemGroupHeaderLayoutType]: ItemGroupHeaderLayoutType.Modern,

  // T-946: Dark mial wlasny styl ulicy Mapboxa i naglowki modali bez tla - zostaja (reszta z Fiord Night)
  [ThemeVariableType.MapboxStreetStyleUrl]: 'mapbox://styles/hypris/cm7ap2wkc004r01qyfwby2vuf',
  [ThemeVariableType.ShouldShowModalHeaderComponentBackground]: false,
  [ThemeVariableType.PaletteColors]: PaletteColors,
  [ThemeVariableType.PaletteColorMatrix]: PaletteColorMatrix,
}

// T-946 PR 1A (tura 2): wykresy, kanwa automatyzacji i tablica od tokenow Dark (jego OnPrimary, ContentDarkest,
// ContainerLow, ContainerHigh, PrimaryContainer) - to, co Dark rysuje dzis; literaly pluginow, tooltip Chart.js
// i podpis modulu - z Fiord Night
export const DarkThemeVariables: IThemeVariables = {
  ...DarkBaseThemeVariables,
  ...getFiordNightDerivedVariables(DarkBaseThemeVariables),
}
